import React, { useRef, useContext, useEffect, useState } from 'react'
import styled, { ThemeContext } from 'styled-components'
import { useTranslation } from 'react-i18next'

// Styles
import { mqFrom } from '../../styles/mediaQueries'

// Utils
import { useAppState } from '../../context/AppContext'
import { formatDate, formatTime } from '../../utils/dateHelper'
import { getSystemMessages } from '../../utils/journalMessageHelper'
import { contextButton, IMenuLink } from '../ContextButton'
import { findCompanyMemberById } from '../../common/helpers/company.helper'

// Components
import Avatar from '../Avatar'
import Text, { MarkDownText } from '../Text'
import { Icon } from '../icon'
import { RowAligned } from '../layout/LayoutAtoms'
import AvatarIcon from '../AvatarIcon'
import ExternIcon from '../icon/ExternIcon'

// Types
import { IconType } from '../icon/IconType'
import { GoalChatMessageType, IGoalMessage } from '../../common/models/Pdp'
import { FeedbackDirection } from '../../common/models/Feedback'
import { IJournalEntry, JournalEntryType } from '../../common/models/Journal'

type Variant = 'goal' | 'feedback'

interface IJournalEntryMessage extends IJournalEntry {
  messageType: JournalEntryType
  externalUser?: string
  dateSubmitted?: Date
}

export interface IFormFeedback {
  _id?: any
  dateCreated: Date
  dateSubmitted?: Date
  senderId?: string
  externalUser?: string
  content: string | React.ReactNode
  messageType: FeedbackDirection | 'anonymous' | 'text'
}

interface IJournalMessage {
  message: IGoalMessage | IFormFeedback | IJournalEntryMessage
  variant: Variant
  externalUser?: string
  handleDelete?: () => void
  handleEdit?: () => void
  firstElement?: boolean
}
interface IProps {
  className?: string
  variant?: Variant
  startJournalMessage?: string
  startJournalIcon?: IconType
  startJournalComponent?: React.ReactNode
  externalUser?: string
  reversedColors?: boolean
  handleDelete?: (journalId: any) => void
  handleEdit?: (journalId: any) => void
  journals: Array<IGoalMessage | IFormFeedback | IJournalEntryMessage>
}

export const JournalMessage = React.forwardRef(
  (
    {
      message,
      variant,
      handleDelete,
      externalUser,
      firstElement,
      handleEdit,
    }: IJournalMessage,
    ref: React.ForwardedRef<HTMLDivElement | null>
  ) => {
    const { theme, user, company } = useAppState()
    const member = user?.asCompanyMember
    const { t } = useTranslation()

    const isOwnMessage = message.senderId === member?._id
    const messageOwner =
      company && findCompanyMemberById(company, message.senderId)

    const systemMessages: Array<typeof message.messageType> = [
      GoalChatMessageType.GOAL_CREATED,
      GoalChatMessageType.PDP_CHANGED,
      GoalChatMessageType.PDP_COPIED,
      JournalEntryType.LOCK_STATUS_CHANGE,
      JournalEntryType.PARTICIPANT_ADD,
      JournalEntryType.PARTICIPANT_REMOVE,
      JournalEntryType.SYSTEM_ENTRY,
      JournalEntryType.TITLE_CHANGE,
      JournalEntryType.LOST_ACCESS,
    ]

    const links: IMenuLink[] = []

    if (handleDelete) {
      links.push({
        label: t('general.actions.remove'),
        icon: 'TrashCan',
        onClick: handleDelete,
      })
    }

    if (handleEdit) {
      links.push({
        label: t('general.actions.edit'),
        icon: 'Pencil',
        onClick: handleEdit,
      })
    }

    const { buttonComponent } = contextButton([
      {
        links: links.reverse(),
      },
    ])

    return (
      <MessageContainer ref={ref} variant={variant} firstElement={firstElement}>
        {!systemMessages.includes(message.messageType) ? (
          <>
            <RowAligned>
              {message.content &&
              variant === 'feedback' &&
              (message.messageType === FeedbackDirection.TIP ||
                message.messageType === FeedbackDirection.TOP) ? (
                <AvatarIcon
                  switchOrder={true}
                  user={externalUser ? undefined : messageOwner}
                  iconType={
                    message.messageType === FeedbackDirection.TIP
                      ? 'Light'
                      : 'Thumb'
                  }
                  version="feedback"
                />
              ) : externalUser ? (
                <StyledExternIcon
                  size={20}
                  color={theme.colors.textLight}
                  backgroundSize={36}
                  backgroundColor="primaryLight"
                />
              ) : (
                <StyledAvatar user={messageOwner} />
              )}

              <div>
                <Text
                  textStyle="labelHeavy"
                  color={isOwnMessage ? 'primary' : 'text'}
                >
                  {messageOwner
                    ? messageOwner.firstName
                    : externalUser || 'Former team member'}
                </Text>
                <Text
                  textStyle="caption"
                  color="textLighter"
                  marginTop={4}
                >{`${formatDate(
                  message.dateSubmitted ?? message.dateCreated
                )} – ${formatTime(
                  message.dateSubmitted ?? message.dateCreated
                )}${
                  'dateModified' in message && message.dateModified
                    ? ` ${t('userProfileTermReviews.noteEdited')}`
                    : ''
                }`}</Text>
              </div>
              {(isOwnMessage || (variant === 'feedback' && externalUser)) &&
                links.length > 0 && (
                  <StyledButton>{buttonComponent()}</StyledButton>
                )}
            </RowAligned>
            <Details>
              {message.content && message.messageType !== 'anonymous' && (
                <MarkDownText textStyle="bodySmall">
                  {message.content.toString()}
                </MarkDownText>
              )}
              {message.content && message.messageType === 'anonymous' && (
                <Text textStyle="bodySmall">{message.content}</Text>
              )}
            </Details>
          </>
        ) : (
          <SystemMessage>
            <Text textStyle="caption">
              {message.messageType === GoalChatMessageType.GOAL_CREATED
                ? t(`goalMessages.labelGoalCreated`, {
                    name: messageOwner?.firstName,
                  })
                : message.messageType === GoalChatMessageType.PDP_CHANGED
                ? t(`goalMessages.labelGoalEdited`, {
                    name: messageOwner?.firstName,
                  })
                : message.messageType === GoalChatMessageType.PDP_COPIED
                ? t('addNewGoal.goalRepublished')
                : 'entryType' in message
                ? getSystemMessages(message, company, messageOwner)
                : ''}
            </Text>
            <Text
              textStyle="captionSmall"
              color="text72"
              marginTop={3}
              marginLeft={12}
            >{`${formatDate(
              message.dateSubmitted ?? message.dateCreated
            )}`}</Text>
          </SystemMessage>
        )}
      </MessageContainer>
    )
  }
)

const Journal: React.FC<IProps> = ({
  className,
  variant = 'goal',
  handleDelete,
  handleEdit,
  journals,
  startJournalComponent,
  startJournalMessage,
  startJournalIcon,
  externalUser,
  reversedColors,
}) => {
  const container = useRef<HTMLDivElement>(null)
  const theme = useContext(ThemeContext)
  const { t } = useTranslation()
  const [showScrollMessage, setShowScrollMessage] = useState<boolean>(false)

  const handleScroll = () => {
    if (!container || !container.current) return
    if (container.current.scrollTop < 24) {
      setShowScrollMessage(false)
    }
    if (
      container.current.scrollHeight > container.current.clientHeight &&
      container.current.scrollTop > 23 &&
      !showScrollMessage
    ) {
      setShowScrollMessage(true)
    }
    if (
      (container.current.scrollTop < 24 ||
        container.current.scrollHeight <= container.current.clientHeight) &&
      showScrollMessage
    ) {
      setShowScrollMessage(false)
    }
  }

  useEffect(() => {
    if (!container || !container.current || !window || !journals) {
      return
    }
    handleScroll()
    // Scroll to bottom of journal
    container?.current?.scroll({
      behavior: 'smooth',
      top: container?.current?.scrollHeight,
    })

    container.current.addEventListener('scroll', handleScroll)

    return () => {
      container?.current?.removeEventListener('scroll', handleScroll)
    }
  }, [journals])

  return (
    <Container className={className} ref={container} reversed={reversedColors}>
      <ScrollMessage show={showScrollMessage}>
        <Icon type="ArrowUp" color={theme.colors.primary} size={16} />
        <Text
          textStyle="captionSmall"
          color="primary"
          textAlign="center"
          marginRight={8}
        >
          {t('journal.scrollUp')}
        </Text>
        <Icon type="ArrowUp" color={theme.colors.primary} size={16} />
      </ScrollMessage>
      {startJournalMessage && (
        <StartMessage>
          <Icon
            color={theme.colors.textLight}
            type={startJournalIcon || 'Journal'}
            size={16}
          />
          <Text textStyle="caption">{startJournalMessage}</Text>
        </StartMessage>
      )}
      {startJournalComponent && startJournalComponent}
      {journals &&
        journals.length > 0 &&
        journals.map((journal, index) => (
          <JournalMessage
            message={journal}
            key={`journal-${index}`}
            externalUser={externalUser || journal.externalUser}
            variant={variant}
            firstElement={!startJournalMessage && index === 0}
            handleDelete={
              handleDelete ? () => handleDelete(journal._id) : undefined
            }
            handleEdit={handleEdit ? () => handleEdit(journal._id) : undefined}
          />
        ))}
    </Container>
  )
}

const StartMessage = styled(RowAligned)`
  background-color: ${({ theme }) => theme.colors.textLightest};
  border-radius: 40px;
  padding: 8px 12px;
  margin: auto auto 16px;
`

const ScrollMessage = styled(RowAligned)<{ show: boolean }>`
  position: sticky;
  width: fit-content;
  top: 4px;
  left: 50%;
  transform: translateX(-50%);
  opacity: ${({ show }) => (show ? 1 : 0)};
  border-radius: 500px;
  background-color: ${({ theme }) => theme.colors.textLightest};
  padding: 4px 12px;
`

const SystemMessage = styled(RowAligned)`
  justify-content: space-between;
  align-items: flex-start;
  background-color: ${({ theme }) => theme.colors.textLightest};
  border-radius: 12px;
  padding: 12px 16px;
  width: 100%;
`

const Container = styled.div<{ reversed?: boolean }>`
  padding: 0 16px 12px;
  border-radius: 12px;
  margin-top: 12px;
  background-color: ${({ reversed, theme }) =>
    reversed ? theme.colors.lightest : theme.colors.primaryLight};
  display: flex;
  flex-direction: column;
  max-height: 320px;
  overflow: auto;
  position: relative;
  border-top: 12px solid
    ${({ theme, reversed }) =>
      reversed ? theme.colors.lightest : theme.colors.primaryLight};
  border-bottom: 12px solid
    ${({ theme, reversed }) =>
      reversed ? theme.colors.lightest : theme.colors.primaryLight};

  ${mqFrom.S`
    padding: 0 24px 12px;
  `}
`

const Details = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 12px;

  ${mqFrom.M`
    flex-direction: row;
  `}
`

const StyledButton = styled.div`
  margin-left: auto;
`

const MessageContainer = styled.div<{
  variant: Variant
  firstElement?: boolean
}>`
  display: flex;
  flex-direction: column;
  width: 100%;

  ${({ firstElement }) =>
    firstElement &&
    `
      margin-top: auto;
  `}

  &:not(:last-of-type) {
    margin-bottom: 24px;
  }
`

const StyledAvatar = styled(Avatar)`
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  margin-right: 16px;
`

const StyledExternIcon = styled(ExternIcon)`
  margin-right: 16px !important;
`

export default Journal
